import React, { useContext, useEffect, useRef, useState } from 'react';
import {
    Accordion,
    AccordionDetails,
    AccordionSummary,
    Button,
    CircularProgress,
    Dialog,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Tab,
    Tabs,
    Typography,
} from '@material-ui/core';
import { makeStyles, useTheme } from '@material-ui/core/styles';
import ZoomInIcon from '@material-ui/icons/ZoomIn';
import ZoomOutIcon from '@material-ui/icons/ZoomOut';

import { packagingApi } from 'api/packaging';
import { procurementApi } from 'api/procurement';
import { qualityApi } from 'api/quality';
import { receivingApi } from 'api/receiving';
import { ViewItemsTable } from 'components/procurement/components/viewPurchaseOrder';
import { UserContext } from 'contexts/UserContext';
import { DataTable, Notifications, TabPanel } from 'shared';
import { useIsFeatureOn } from 'shared/hooks/featureFlags/useIsFeatureOn';
import dayjs from 'utils/dayjsConfig';

import { ItemDidNotArriveModal } from './itemDidNotArriveModal';
import { QualityReceivingModuleResetModal } from './qualityReceivingModuleResetModal';
import { fetchReceivableLineItems } from './AllocatedReceiving/fetchReceivableLineItems';
import { ReceiveAllocatedLineItemModal } from './AllocatedReceiving/ReceiveAllocatedLineItemModal';
import { ReceiveLineItemWithPalletsModal } from './directedReceiving/receiveLineItemWithPalletsModal';
import { PrintConsumedPalletAndCaseLabelsModal } from './receivedLabels/PrintConsumedLotLabels';
import { PrintReceivedPalletAndCaseLabelsModal } from './receivedLabels/printReceivedPalletAndCaseTagsModal';

const BAMS_RECEIVING_MODULE = 'BAMS_RECEIVING_MODULE';

const colors = {
    blue: '#485bbd',
    highlighter: '#fbfe42',
    red: '#9F0000',
    green: '#0C892F',
};

const statusColors = {
    OPEN: '#FAFF00',
    SUBMITTED: '#00FF29',
    ISSUE: '#FF0000',
    IN_PROGRESS: '#FF7A00',
    PLACED: '#0500FF',
    RECEIVED: '#AD00FF',
    PAUSED: '#6699cc',
    QC_COMPLETE: '#00FF29',
    PAID: 'black',
    PENDING_REJECTION: '#e65a5a',
    REJECTED: '#FF0000',
    REJECTION_DENIED: '#023020',
};

const statusTextColors = {
    IN_PROGRESS: 'black',
    OPEN: 'black',
    ISSUE: 'white',
    PLACED: 'white',
    RECEIVED: 'white',
    PAUSED: 'white',
    SUBMITTED: 'black',
    QC_COMPLETE: 'black',
    PAID: 'white',
    PENDING_REJECTION: 'black',
    REJECTED: 'white',
    REJECTION_DENIED: 'white',
};

const useStyles = makeStyles((theme) => ({
    panelContainer: {
        borderColor: theme.palette.ivy.main,
        borderWidth: '3px',
        borderStyle: 'solid',
        borderRadius: '5px',
        margin: '10px 10px',
        padding: '0px',
    },
    panelSummary: {
        height: '70px',
        fontSize: '16px',
        padding: '25px',
    },
}));

function ModifyPOModal({ close, open, rowData }) {
    return (
        <Dialog open={open} onClose={close} fullWidth maxWidth="lg" height="90%">
            <DialogTitle>Modify Purchase Order {rowData.id}</DialogTitle>
            <DialogContent>
                <ViewItemsTable receivingPOID={rowData.id} />
            </DialogContent>
        </Dialog>
    );
}

function DirectedReceivingItemModal({ closeModal, editEnabled, issueCodes, photoRows, rowData, setNotification }) {
    const [error, setError] = useState(null);
    const [pageIdx, setPageIdx] = useState(rowData.initialPageIdx);
    const [poMapID, setPoMapID] = useState(rowData.poMapID);
    const [poID, setPoID] = useState(rowData.poID);
    const [qualityReceivingData, setQualityReceivingData] = useState(rowData.qualityReceivingData);
    const [caseCounts, setCaseCounts] = useState(() => {
        const counts = rowData.qualityReceivingData?.caseCounts ?? {};
        if (Array.isArray(counts)) {
            return counts;
        }
        return Object.keys(counts).map((key) => counts[key]);
    });
    const [selectedInternalInspectionTab, setSelectedInternalInspectionTab] = useState('0');
    const [selectedExternalInspectionTab, setSelectedExternalInspectionTab] = useState('0');
    const [selectedUnitCountTab, setSelectedUnitCountTab] = useState('0');
    const [expanded, setExpanded] = useState(false);
    const [enlargedPhotoID, setEnlargedPhotoID] = useState(null);

    const handleInternalInspectionTabChange = (event, value) => {
        setSelectedInternalInspectionTab(value);
    };
    const handleExternalInspectionTabChange = (event, value) => {
        setSelectedExternalInspectionTab(value);
    };

    const handleUnitCountTabChange = (event, value) => {
        setSelectedUnitCountTab(value);
    };

    const handleAccordionChange = (panel) => (event, isExpanded) => {
        setExpanded(isExpanded ? panel : false);
    };

    const externalPhotoCodes = [
        'PRODUCT_SAMPLE',
        'VENDOR_BARCODE',
        'NUTRITION_LABEL',
        'CLOSE_UP_EXTERNAL',
        'EXTERNAL_COLLECTION_ANGLE_1',
        'EXTERNAL_COLLECTION_ANGLE_2',
        'EXTERNAL_COLLECTION_ANGLE_3',
        'EXTERNAL_COLLECTION_ANGLE_4',
        'EXTERNAL_SCALE_PHOTO',
    ];

    const internalPhotoCodes = [
        'PULP_TEMP',
        'CLOSE_UP_INTERNAL',
        'INTERNAL_COLLECTION_ANGLE_1',
        'INTERNAL_COLLECTION_ANGLE_2',
        'INTERNAL_COLLECTION_ANGLE_3',
        'INTERNAL_COLLECTION_ANGLE_4',
        'INTERNAL_SCALE_PHOTO',
    ];

    const caseCountPhotoCodes = ['UPC_BARCODE', 'BROKEN_UNITS'];

    useEffect(() => {
        setPageIdx(rowData.initialPageIdx);
    }, []);

    const getExternalSummaryTableData = () => {
        const formattedSummaryData = {};
        Object.keys(qualityReceivingData.externalIssues ?? {}).forEach((key) => {
            const { issues } = qualityReceivingData.externalIssues[key];
            if (issues) {
                issues.forEach((issue) => {
                    if (!formattedSummaryData[issue.issueType]) {
                        formattedSummaryData[issue.issueType] = {
                            cases: 0,
                            units: 0,
                        };
                    }
                    formattedSummaryData[issue.issueType].cases++;
                    formattedSummaryData[issue.issueType].units += parseFloat(issue.unitsDamaged);
                });
            }
        });

        const formattedDataArray = [];
        Object.keys(formattedSummaryData).forEach((key) => {
            formattedDataArray.push({
                id: key,
                issueType: key,
                casesInspected: formattedSummaryData[key].cases,
                unitsDamaged: formattedSummaryData[key].units,
            });
        });

        return formattedDataArray;
    };

    const summaryDataTableCols = [
        { accessor: 'issueType', Header: 'Damage Reason', editable: false, useFilter: false, sortable: false },
        { accessor: 'casesInspected', Header: 'Number of Cases', editable: false, useFilter: false, sortable: false },
        { accessor: 'unitsDamaged', Header: 'Unit Qty', editable: false, useFilter: false, sortable: false },
    ];

    const getInternalSummaryTableData = () => {
        const formattedSummaryData = {};
        Object.keys(qualityReceivingData.internalIssues ?? {}).forEach((key) => {
            const { issues } = qualityReceivingData.internalIssues[key];
            if (issues) {
                issues.forEach((issue) => {
                    if (!formattedSummaryData[issue.issueType]) {
                        formattedSummaryData[issue.issueType] = {
                            cases: 0,
                            units: 0,
                        };
                    }
                    formattedSummaryData[issue.issueType].cases++;
                    formattedSummaryData[issue.issueType].units += parseFloat(issue.unitsDamaged);
                });
            }
        });

        const formattedDataArray = [];
        Object.keys(formattedSummaryData).forEach((key) => {
            formattedDataArray.push({
                id: key,
                issueType: key,
                casesInspected: formattedSummaryData[key].cases,
                unitsDamaged: formattedSummaryData[key].units,
            });
        });

        return formattedDataArray;
    };

    const getExternalUnitsDamagedPercent = () => {
        let unitsDamaged = 0;
        let unitsInspected = 0;
        Object.keys(qualityReceivingData.externalIssues ?? {}).forEach((key) => {
            unitsInspected += qualityReceivingData.externalIssues[key].unitsInspected || 0;
            const { issues } = qualityReceivingData.externalIssues[key];
            if (issues) {
                issues.forEach((issue) => {
                    unitsDamaged += parseFloat(issue.unitsDamaged);
                });
            }
        });
        if (unitsInspected == 0) {
            return 0;
        }
        return unitsDamaged / unitsInspected;
    };

    const getInternalUnitsDamagedPercent = () => {
        let unitsDamaged = 0;
        let unitsInspected = 0;
        Object.keys(qualityReceivingData.internalIssues ?? {}).forEach((key) => {
            unitsInspected += qualityReceivingData.internalIssues[key].unitsInspected || 0;
            const { issues } = qualityReceivingData.internalIssues[key];
            if (issues) {
                issues.forEach((issue) => {
                    unitsDamaged += parseFloat(issue.unitsDamaged);
                });
            }
        });
        if (unitsInspected == 0) {
            return 0;
        }
        return unitsDamaged / unitsInspected;
    };

    const unitCountSummaryDataTableCols = [
        { accessor: 'caseIdx', Header: 'Case Number', editable: false, useFilter: false, sortable: false },
        { accessor: 'unitCount', Header: 'Number of Units', editable: editEnabled, useFilter: false, sortable: false },
    ];

    const unitWeightSummaryDataTableCols = [
        { accessor: 'unitNumber', Header: 'unitNumber', editable: false, useFilter: false, sortable: false },
        { accessor: 'weight', Header: 'Weight(lbs)', editable: editEnabled, useFilter: false, sortable: false },
    ];

    const getFormattedWeightSummaryData = (caseIdx) => {
        const caseWeights = caseCounts[caseIdx];
        const formattedData = [];
        if (caseWeights.unitWeights) {
            caseWeights.unitWeights.forEach((unitWeight, idx) => {
                formattedData.push({
                    id: idx,
                    unitNumber: idx + 1,
                    weight: unitWeight,
                });
            });
        }
        return formattedData;
    };

    const inspectionTable = [
        {
            id: 'pallets',
            label: '# of PALLETS Inspected',
            value: qualityReceivingData.palletsInspectedExternal,
        },
        {
            id: 'cases',
            label: '# of CASES Inspected',
            value: qualityReceivingData.casesInspectedExternal,
        },
        {
            id: 'units',
            label: '# of UNITS Inspected',
            value: Object.values(qualityReceivingData.externalIssues ?? {}).reduce(
                (sum, issue) => sum + (issue.unitsInspected || 0),
                0,
            ),
        },
    ];

    const inspectionTableInternal = [
        {
            id: 'pallets',
            label: '# of PALLETS Inspected',
            value: qualityReceivingData.palletsInspectedInternal,
        },
        {
            id: 'cases',
            label: '# of CASES Inspected',
            value: qualityReceivingData.casesInspectedInternal,
        },
        {
            id: 'units',
            label: '# of UNITS Inspected',
            value: Object.values(qualityReceivingData.internalIssues ?? {}).reduce(
                (sum, issue) => sum + (issue.unitsInspected || 0),
                0,
            ),
        },
        {
            id: 'pulpTemp',
            label: 'Pulp Temp(˚F)',
            value: qualityReceivingData.levelTwoPulpTemp,
        },
    ];

    const unitsPerCaseTable = [
        {
            id: 'casesCounted',
            label: '# of CASES Inspected',
            value: qualityReceivingData.casesCounted,
        },
    ];

    const inspectionCols = [
        { accessor: 'label', Header: '', editable: false, useFilter: false, sortable: false },
        {
            accessor: 'value',
            Header: '',
            editable: (row) => editEnabled && row.original.id !== 'units',
            useFilter: false,
            sortable: false,
        },
    ];

    const getUnitCountSummaryData = () => {
        const formattedSummaryData = [];
        if (qualityReceivingData.caseCounts) {
            Object.keys(qualityReceivingData.caseCounts).forEach((caseIdx) => {
                formattedSummaryData.push({
                    id: caseIdx,
                    caseIdx,
                    unitCount: qualityReceivingData.caseCounts[caseIdx].unitCount,
                });
            });
        }
        return formattedSummaryData;
    };

    const getBackgroundHeaderColor = (pageIdx) => {
        if (pageIdx == 0) {
            return '#0C892F';
        }
        if (pageIdx == 1) {
            return '#3F51B5';
        }
        if (pageIdx == 2) {
            return '#9F0000';
        }
    };

    return (
        <Dialog
            fullWidth
            maxWidth="md"
            open
            onClose={() => {
                setEnlargedPhotoID(null);
                closeModal();
            }}
        >
            <DialogTitle
                style={{ backgroundColor: getBackgroundHeaderColor(pageIdx), color: 'white', textAlign: 'center' }}
            >
                {pageIdx == 1 ? 'External Issues' : pageIdx == 0 ? 'Units per Case' : 'Internal Issues'}
            </DialogTitle>

            <DialogContent>
                <div
                    style={{
                        outline: 0,
                        backgroundColor: 'white',
                        width: '700px',
                        margin: '10% auto',
                        padding: '10px',
                    }}
                >
                    {pageIdx == 0 && (
                        <div>
                            <Paper>
                                <Tabs
                                    onChange={handleUnitCountTabChange}
                                    value={selectedUnitCountTab}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    centered
                                >
                                    <Tab label="Summary" value="0" />
                                    <Tab label="Photos" value="1" />
                                </Tabs>
                                <TabPanel selectedTab={selectedUnitCountTab} index="0">
                                    <div>
                                        <h3>% DISCREPANCY: {qualityReceivingData.unitCountVariance || 0}</h3>
                                    </div>
                                    <DataTable
                                        data={unitsPerCaseTable}
                                        columns={inspectionCols}
                                        editable={editEnabled}
                                        showPagination={false}
                                        saveEdit={async ({ field, id, value }) => {
                                            const response = await qualityApi.recordCasesCounted(
                                                qualityReceivingData.id,
                                                value,
                                                'external',
                                            );
                                            if (response.status === false) {
                                                let message = 'Error';
                                                if (response) {
                                                    message += `: ${response.msg}`;
                                                }
                                                setNotification({ text: message, level: 'error' });
                                            } else {
                                                setNotification({ text: 'Cases Inspected recorded', level: 'success' });
                                                setQualityReceivingData(response.data.itemInspected);
                                            }
                                        }}
                                    />

                                    {!(
                                        qualityReceivingData.baseUnitsPerCase >= 500 &&
                                        qualityReceivingData.purchasingCaseUom == 'lb'
                                    ) && (
                                        <DataTable
                                            data={getUnitCountSummaryData()}
                                            columns={unitCountSummaryDataTableCols}
                                            editable={editEnabled}
                                            showPagination={false}
                                            saveEdit={async ({ field, id, value }) => {
                                                const response = await qualityApi.recordCaseUnitCountValue(
                                                    qualityReceivingData.id,
                                                    id,
                                                    value,
                                                );
                                                if (response.status === false) {
                                                    let message = 'Error';
                                                    if (response) {
                                                        message += `: ${response.msg}`;
                                                    }
                                                    setNotification({ text: message, level: 'error' });
                                                } else {
                                                    setNotification({
                                                        text: 'Case unit count recorded',
                                                        level: 'success',
                                                    });
                                                    setQualityReceivingData(response.data.itemInspected);
                                                }
                                            }}
                                        />
                                    )}
                                    {qualityReceivingData.baseUnitsPerCase >= 500 &&
                                        qualityReceivingData.purchasingCaseUom == 'lb' && (
                                            <div>
                                                {caseCounts &&
                                                    caseCounts.map((caseCounted, caseIdx) => (
                                                        <Accordion
                                                            expanded={expanded == `case_${caseIdx}`}
                                                            onChange={handleAccordionChange(`case_${caseIdx}`)}
                                                            key={`case_${caseIdx}`}
                                                        >
                                                            <AccordionSummary
                                                                aria-controls="panel1bh-content"
                                                                id="panel1bh-header"
                                                            >
                                                                <Typography sx={{ width: '33%', flexShrink: 0 }}>
                                                                    {`Case ${caseIdx + 1}`}
                                                                </Typography>
                                                                <AccordionDetails>
                                                                    {caseCounted.unitWeights && (
                                                                        <DataTable
                                                                            data={getFormattedWeightSummaryData(
                                                                                caseIdx,
                                                                            )}
                                                                            columns={unitWeightSummaryDataTableCols}
                                                                            editable={editEnabled}
                                                                            showPagination={false}
                                                                            saveEdit={async ({ field, id, value }) => {
                                                                                const response =
                                                                                    await qualityApi.recordCaseUnitWeightValue(
                                                                                        qualityReceivingData.id,
                                                                                        caseIdx,
                                                                                        id,
                                                                                        value,
                                                                                    );
                                                                                if (response.status === false) {
                                                                                    let message = 'Error';
                                                                                    if (response) {
                                                                                        message += `: ${response.msg}`;
                                                                                    }
                                                                                    setNotification({
                                                                                        text: message,
                                                                                        level: 'error',
                                                                                    });
                                                                                } else {
                                                                                    setNotification({
                                                                                        text: 'Unit weight recorded',
                                                                                        level: 'success',
                                                                                    });
                                                                                    setQualityReceivingData(
                                                                                        response.data.itemInspected,
                                                                                    );
                                                                                }
                                                                            }}
                                                                        />
                                                                    )}
                                                                </AccordionDetails>
                                                            </AccordionSummary>
                                                        </Accordion>
                                                    ))}
                                            </div>
                                        )}
                                </TabPanel>
                                <TabPanel selectedTab={selectedUnitCountTab} index="1">
                                    {enlargedPhotoID && (
                                        <div>
                                            <div
                                                style={{ position: 'relative', cursor: 'pointer' }}
                                                onClick={() => {
                                                    setEnlargedPhotoID(null);
                                                }}
                                            >
                                                <ZoomOutIcon
                                                    style={{ position: 'absolute', top: 0, right: 0, color: 'white' }}
                                                />
                                                <img
                                                    src={photoRows.find((photo) => photo.id === enlargedPhotoID).s3URL}
                                                    alt={photoRows.find((photo) => photo.id === enlargedPhotoID).code}
                                                    width="100%"
                                                />
                                            </div>

                                            <div
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                }}
                                            >
                                                <Button
                                                    onClick={() => {
                                                        procurementApi.downloadPODocument(
                                                            poID,
                                                            enlargedPhotoID,
                                                            photoRows
                                                                .find((photo) => photo.id === enlargedPhotoID)
                                                                .documentURL.replace(/^.*[\\\/]/, ''),
                                                        );
                                                    }}
                                                >
                                                    Download
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {!enlargedPhotoID && (
                                        <Grid container>
                                            {photoRows.map((photo) => {
                                                if (
                                                    photo.code &&
                                                    photo.purchaseOrderMapID === poMapID &&
                                                    (photo.code.startsWith('EXPIRATION_DATE') == true ||
                                                        caseCountPhotoCodes.includes(photo.code) == true)
                                                ) {
                                                    return (
                                                        <Grid item xs={4} key={photo.code}>
                                                            <div
                                                                style={{ position: 'relative', cursor: 'pointer' }}
                                                                onClick={() => setEnlargedPhotoID(photo.id)}
                                                            >
                                                                <ZoomInIcon
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        right: 20,
                                                                        color: 'white',
                                                                    }}
                                                                />
                                                                <img src={photo.s3URL} alt={photo.code} width="90%" />
                                                            </div>
                                                            <div
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                }}
                                                            >
                                                                <Button
                                                                    onClick={() => {
                                                                        procurementApi.downloadPODocument(
                                                                            poID,
                                                                            photo.id,
                                                                            photo.documentURL.replace(/^.*[\\\/]/, ''),
                                                                        );
                                                                    }}
                                                                >
                                                                    Download
                                                                </Button>
                                                            </div>
                                                        </Grid>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </Grid>
                                    )}
                                </TabPanel>
                            </Paper>
                        </div>
                    )}
                    {pageIdx == 1 && (
                        <div>
                            <Paper>
                                <Tabs
                                    onChange={handleExternalInspectionTabChange}
                                    value={selectedExternalInspectionTab}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    centered
                                >
                                    <Tab label="Summary" value="0" />
                                    <Tab label="Photos" value="1" />
                                </Tabs>
                                <TabPanel selectedTab={selectedExternalInspectionTab} index="0">
                                    <div>
                                        <h3>% ITEMS DAMAGED: {(getExternalUnitsDamagedPercent() * 100).toFixed(2)}</h3>
                                    </div>
                                    <DataTable
                                        data={inspectionTable}
                                        columns={inspectionCols}
                                        editable={editEnabled}
                                        showPagination={false}
                                        saveEdit={async ({ field, id, value }) => {
                                            if (id == 'cases') {
                                                const response = await qualityApi.recordCasesInspected(
                                                    qualityReceivingData.id,
                                                    value,
                                                    'external',
                                                );
                                                if (response.status === false) {
                                                    let message = 'Error';
                                                    if (response) {
                                                        message += `: ${response.msg}`;
                                                    }
                                                    setNotification({ text: message, level: 'error' });
                                                } else {
                                                    setNotification({
                                                        text: 'Cases Inspected recorded',
                                                        level: 'success',
                                                    });
                                                    setQualityReceivingData(response.data.itemInspected);
                                                }
                                            } else if (id == 'pallets') {
                                                const response = await qualityApi.recordPalletsInspected(
                                                    qualityReceivingData.id,
                                                    value,
                                                    'external',
                                                );
                                                if (response.status === false) {
                                                    let message = 'Error';
                                                    if (response) {
                                                        message += `: ${response.msg}`;
                                                    }
                                                    setNotification({ text: message, level: 'error' });
                                                } else {
                                                    setNotification({
                                                        text: 'Pallets Inspected recorded',
                                                        level: 'success',
                                                    });
                                                    setQualityReceivingData(response.data.itemInspected);
                                                }
                                            }
                                        }}
                                    />
                                    <DataTable
                                        data={getExternalSummaryTableData()}
                                        columns={summaryDataTableCols}
                                        editable
                                        showPagination={false}
                                        saveEdit={async ({ field, id, value }) => {
                                            const updates = {
                                                key: field,
                                                value,
                                            };
                                            if (id == '') {
                                                id = 0;
                                            }
                                            const response = await qualityApi.recordQualityReceivingIssueV2(
                                                id,
                                                updates,
                                                poMapID,
                                                'external',
                                            );
                                            if (response.status === false) {
                                                let message = 'Error';
                                                if (response) {
                                                    message += `: ${response.msg}`;
                                                }
                                                setNotification({ text: message, level: 'error' });
                                            } else {
                                                setNotification({ text: 'External issue recorded!', level: 'success' });
                                                setQualityReceivingData(response.data.itemInspected);
                                            }
                                        }}
                                    />
                                </TabPanel>
                                <TabPanel selectedTab={selectedExternalInspectionTab} index="1">
                                    {enlargedPhotoID && (
                                        <div>
                                            <div
                                                style={{ position: 'relative', cursor: 'pointer' }}
                                                onClick={() => {
                                                    setEnlargedPhotoID(null);
                                                }}
                                            >
                                                <ZoomOutIcon
                                                    style={{ position: 'absolute', top: 0, right: 0, color: 'white' }}
                                                />
                                                <img
                                                    src={photoRows.find((photo) => photo.id === enlargedPhotoID).s3URL}
                                                    alt={photoRows.find((photo) => photo.id === enlargedPhotoID).code}
                                                    width="100%"
                                                />
                                            </div>

                                            <div
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                }}
                                            >
                                                <Button
                                                    onClick={() => {
                                                        procurementApi.downloadPODocument(
                                                            poID,
                                                            enlargedPhotoID,
                                                            photoRows
                                                                .find((photo) => photo.id === enlargedPhotoID)
                                                                .documentURL.replace(/^.*[\\\/]/, ''),
                                                        );
                                                    }}
                                                >
                                                    Download
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {!enlargedPhotoID && (
                                        <Grid container>
                                            {photoRows.map((photo) => {
                                                if (
                                                    photo.code &&
                                                    photo.purchaseOrderMapID === poMapID &&
                                                    externalPhotoCodes.includes(photo.code) == true
                                                ) {
                                                    return (
                                                        <Grid item xs={4} key={photo.code}>
                                                            <div
                                                                style={{ position: 'relative', cursor: 'pointer' }}
                                                                onClick={() => setEnlargedPhotoID(photo.id)}
                                                            >
                                                                <ZoomInIcon
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        right: 20,
                                                                        color: 'white',
                                                                    }}
                                                                />
                                                                <img src={photo.s3URL} alt={photo.code} width="90%" />
                                                            </div>
                                                            <div
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                }}
                                                            >
                                                                <Button
                                                                    onClick={() => {
                                                                        procurementApi.downloadPODocument(
                                                                            poID,
                                                                            photo.id,
                                                                            photo.documentURL.replace(/^.*[\\\/]/, ''),
                                                                        );
                                                                    }}
                                                                >
                                                                    Download
                                                                </Button>
                                                            </div>
                                                        </Grid>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </Grid>
                                    )}
                                </TabPanel>
                            </Paper>
                        </div>
                    )}
                    {pageIdx == 2 && (
                        <div>
                            <Paper>
                                <Tabs
                                    onChange={handleInternalInspectionTabChange}
                                    value={selectedInternalInspectionTab}
                                    indicatorColor="primary"
                                    textColor="primary"
                                    centered
                                >
                                    <Tab label="Summary" value="0" />
                                    <Tab label="Unit Inspection" value="1" />
                                    <Tab label="Photos" value="2" />
                                </Tabs>
                                <TabPanel selectedTab={selectedInternalInspectionTab} index="0">
                                    <div>
                                        <h3>% ITEMS DAMAGED: {(getInternalUnitsDamagedPercent() * 100).toFixed(2)}</h3>
                                    </div>
                                    <DataTable
                                        data={inspectionTableInternal}
                                        columns={inspectionCols}
                                        editable={editEnabled}
                                        showPagination={false}
                                        saveEdit={async ({ field, id, value }) => {
                                            if (id == 'cases') {
                                                const response = await qualityApi.recordCasesInspected(
                                                    qualityReceivingData.id,
                                                    value,
                                                    'internal',
                                                );
                                                if (response.status === false) {
                                                    let message = 'Error';
                                                    if (response) {
                                                        message += `: ${response.msg}`;
                                                    }
                                                    setNotification({ text: message, level: 'error' });
                                                } else {
                                                    setNotification({
                                                        text: 'Cases Inspected recorded',
                                                        level: 'success',
                                                    });
                                                    setQualityReceivingData(response.data.itemInspected);
                                                }
                                            } else if (id == 'pallets') {
                                                const response = await qualityApi.recordPalletsInspected(
                                                    qualityReceivingData.id,
                                                    value,
                                                    'internal',
                                                );
                                                if (response.status === false) {
                                                    let message = 'Error';
                                                    if (response) {
                                                        message += `: ${response.msg}`;
                                                    }
                                                    setNotification({ text: message, level: 'error' });
                                                } else {
                                                    setNotification({
                                                        text: 'Pallets Inspected recorded',
                                                        level: 'success',
                                                    });
                                                    setQualityReceivingData(response.data.itemInspected);
                                                }
                                            } else if (id == 'pulpTemp') {
                                                const updates = {
                                                    key: 'levelTwoPulpTemp',
                                                    value,
                                                };
                                                const response = await qualityApi.recordQualityReceiving(id, updates);
                                                if (response.status === false) {
                                                    let message = 'Error';
                                                    if (response) {
                                                        message += `: ${response.msg}`;
                                                    }
                                                    setNotification({ text: message, level: 'error' });
                                                } else {
                                                    setNotification({ text: 'Pulp temp recorded', level: 'success' });
                                                    setQualityReceivingData(response.data.itemInspected);
                                                }
                                            }
                                        }}
                                    />
                                    <DataTable
                                        data={getInternalSummaryTableData()}
                                        columns={summaryDataTableCols}
                                        editable
                                        showPagination={false}
                                        saveEdit={async ({ field, id, value }) => {
                                            const updates = {
                                                key: field,
                                                value,
                                            };
                                            if (id == '') {
                                                id = 0;
                                            }
                                            const response = await qualityApi.recordQualityReceivingIssueV2(
                                                id,
                                                updates,
                                                poMapID,
                                                'external',
                                            );
                                            if (response.status === false) {
                                                let message = 'Error';
                                                if (response) {
                                                    message += `: ${response.msg}`;
                                                }
                                                setNotification({ text: message, level: 'error' });
                                            } else {
                                                setNotification({ text: 'External issue recorded!', level: 'success' });
                                                setQualityReceivingData(response.data.itemInspected);
                                            }
                                        }}
                                    />
                                </TabPanel>
                                <TabPanel selectedTab={selectedInternalInspectionTab} index="1">
                                    <Grid container>
                                        {qualityReceivingData.internalUnitInspection &&
                                            Object.keys(qualityReceivingData.internalUnitInspection).map((key) => (
                                                <Grid item xs={3} key={key}>
                                                    <FormControl>
                                                        <InputLabel>{`Unit ${key}`}</InputLabel>
                                                        <Select
                                                            value={qualityReceivingData.internalUnitInspection[key]}
                                                            disabled={!editEnabled}
                                                            onChange={async (e) => {
                                                                if (!editEnabled) {
                                                                    return;
                                                                }
                                                                const res =
                                                                    await qualityApi.recordQualityUnitInspection(
                                                                        qualityReceivingData.id,
                                                                        { index: key, value: e.target.value },
                                                                    );
                                                                if (!res.status) {
                                                                    let message = 'Error';
                                                                    if (res) {
                                                                        message += `: ${res.msg}`;
                                                                    }
                                                                    setNotification({
                                                                        text: message,
                                                                        level: 'error',
                                                                    });
                                                                    return;
                                                                }
                                                                setQualityReceivingData(res.data.itemInspected);
                                                                setNotification({
                                                                    text: 'Unit Inspection recorded',
                                                                    level: 'success',
                                                                });
                                                            }}
                                                        >
                                                            {issueCodes.map((code) => (
                                                                <MenuItem
                                                                    disabled={!editEnabled}
                                                                    value={code}
                                                                    key={code}
                                                                >
                                                                    {code}
                                                                </MenuItem>
                                                            ))}
                                                        </Select>
                                                    </FormControl>
                                                </Grid>
                                            ))}
                                    </Grid>
                                </TabPanel>
                                <TabPanel selectedTab={selectedInternalInspectionTab} index="2">
                                    {enlargedPhotoID && (
                                        <div>
                                            <div
                                                style={{ position: 'relative', cursor: 'pointer' }}
                                                onClick={() => {
                                                    setEnlargedPhotoID(null);
                                                }}
                                            >
                                                <ZoomOutIcon
                                                    style={{ position: 'absolute', top: 0, right: 0, color: 'white' }}
                                                />
                                                <img
                                                    src={photoRows.find((photo) => photo.id === enlargedPhotoID).s3URL}
                                                    alt={photoRows.find((photo) => photo.id === enlargedPhotoID).code}
                                                    width="100%"
                                                />
                                            </div>

                                            <div
                                                onClick={(event) => {
                                                    event.stopPropagation();
                                                }}
                                            >
                                                <Button
                                                    onClick={() => {
                                                        procurementApi.downloadPODocument(
                                                            poID,
                                                            enlargedPhotoID,
                                                            photoRows
                                                                .find((photo) => photo.id === enlargedPhotoID)
                                                                .documentURL.replace(/^.*[\\\/]/, ''),
                                                        );
                                                    }}
                                                >
                                                    Download
                                                </Button>
                                            </div>
                                        </div>
                                    )}
                                    {!enlargedPhotoID && (
                                        <Grid container>
                                            {photoRows.map((photo) => {
                                                if (
                                                    photo.code &&
                                                    photo.purchaseOrderMapID === poMapID &&
                                                    internalPhotoCodes.includes(photo.code) == true
                                                ) {
                                                    return (
                                                        <Grid item xs={4} key={photo.code}>
                                                            <div
                                                                style={{ position: 'relative', cursor: 'pointer' }}
                                                                onClick={() => setEnlargedPhotoID(photo.id)}
                                                            >
                                                                <ZoomInIcon
                                                                    style={{
                                                                        position: 'absolute',
                                                                        top: 0,
                                                                        right: 20,
                                                                        color: 'white',
                                                                    }}
                                                                />
                                                                <img src={photo.s3URL} alt={photo.code} width="90%" />
                                                            </div>
                                                            <div
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                }}
                                                            >
                                                                <Button
                                                                    onClick={() => {
                                                                        procurementApi.downloadPODocument(
                                                                            poID,
                                                                            photo.id,
                                                                            photo.documentURL.replace(/^.*[\\\/]/, ''),
                                                                        );
                                                                    }}
                                                                >
                                                                    Download
                                                                </Button>
                                                            </div>
                                                        </Grid>
                                                    );
                                                }
                                                return null;
                                            })}
                                        </Grid>
                                    )}
                                </TabPanel>
                            </Paper>
                        </div>
                    )}
                    {error && <div style={{ color: 'red' }}>{error}</div>}
                    {}
                </div>
            </DialogContent>
        </Dialog>
    );
}

const EDITABLE_STATUSES = ['PLACED', 'OPEN', 'IN PROGRESS'];
const MAX_UNITS_PER_CASE = 99999;
const VARIANCE_WARNING_THRESHOLD = 10;
const VARIANCE_WARNING_COLOR = '#FFA500';

/**
 * Compute the percentage variance between received and ordered units per case.
 * Returns null if either input is missing/zero (for ordered), otherwise { percentage, isWarning }.
 */
function computeVariance(received, ordered) {
    if (received == null || !ordered) return null;
    const pct = (Math.abs(received - ordered) / ordered) * 100;
    const rounded = Math.round(pct * 10) / 10;
    return { percentage: rounded, isWarning: rounded > VARIANCE_WARNING_THRESHOLD };
}

function UnitsPerCaseInput({
    initialOverride,
    onNotification,
    onSaved,
    orderedValue,
    packagingPurchaseOrderMapGroupId,
    poMapID,
    progressStatus,
    useGroupOverride = false,
}) {
    const [value, setValue] = useState(initialOverride != null ? String(initialOverride) : '');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState(null);
    const previousValue = useRef(value);

    const isEditable = EDITABLE_STATUSES.includes(progressStatus);

    if (!isEditable) {
        return <span>{initialOverride ?? '--'}</span>;
    }

    const save = async () => {
        const trimmed = value.trim();
        if (trimmed === '' || trimmed === String(previousValue.current)) {
            if (trimmed === '' && previousValue.current !== '') {
                setValue(previousValue.current);
            }
            return;
        }

        const parsed = Number(trimmed);
        if (!Number.isSafeInteger(parsed) || parsed < 1 || parsed > MAX_UNITS_PER_CASE) {
            setError('Must be a positive integer');
            setValue(previousValue.current);
            setTimeout(() => setError(null), 3000);
            return;
        }

        setSaving(true);
        setError(null);
        try {
            const response = useGroupOverride
                ? await receivingApi.saveUnitsPerCaseOverrideForGroup(packagingPurchaseOrderMapGroupId, parsed)
                : await receivingApi.saveUnitsPerCaseOverride(poMapID, parsed);
            if (response.status === false) {
                setError(response.msg || 'Save failed');
                setValue(previousValue.current);
                setTimeout(() => setError(null), 3000);
                return;
            }
            previousValue.current = String(parsed);
            onSaved(poMapID, parsed);
            onNotification?.({ text: 'Units per case updated', level: 'success' });
        } catch {
            setError('Save failed');
            setValue(previousValue.current);
            setTimeout(() => setError(null), 3000);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div>
            <input
                type="number"
                min="1"
                step="1"
                value={value}
                placeholder={orderedValue ?? ''}
                disabled={saving}
                onChange={(e) => {
                    setError(null);
                    setValue(e.target.value);
                }}
                onBlur={save}
                onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                        e.target.blur();
                    }
                }}
                style={{ width: '80px' }}
            />
            {saving && <CircularProgress size={14} style={{ marginLeft: 4 }} />}
            {error && <div style={{ color: 'red', fontSize: '0.75rem' }}>{error}</div>}
        </div>
    );
}

// The table to show items v2
function ViewItemsTableV2({ issueCodes, parentRow, setExpandedRow, setUpdatedPurchaseOrderStatus, showReceived }) {
    const [poID, setPOID] = useState(parentRow.original.id);
    const [rows, setRows] = useState([]);
    const [photoRows, setPhotoRows] = useState([]);
    const [printLabelModalDetails, setPrintLabelModalDetails] = useState(null);
    const [printConsumedLotLabelModalDetails, setPrintConsumedLotLabelModalDetails] = useState(null);
    const [modalLaunch, setModalLaunch] = useState({
        poID: parentRow.original.id,
        poMapID: null,
        qualityReceivingData: null,
        initialPageIdx: 0,
    });

    const [itemDocumentationModalOpen, setItemDocumentationModalOpen] = useState(false);

    const [notification, setNotification] = useState({ text: null, level: null });
    const [editEnabled, setEditEnabled] = useState(false);

    const [receiveLineItemWithPalletsModalOpen, setReceiveLineItemWithPalletsModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);

    const [showDidNotArriveModal, setShowDidNotArriveModal] = useState(false);
    const [currentPurchaseOrderMap, setCurrentPurchaseOrderMap] = useState(null);

    const { roles } = useContext(UserContext);
    const allowDidNotArrive = roles?.filter((role) => role.name === 'EDIT_QUALITY_CONTROL_DESKTOP').length > 0;

    const [showQualityReceivingModuleResetModal, setShowQualityReceivingModuleResetModal] = useState(false);

    const [multiTruckPO, setMultiTruckPO] = useState(false);

    const [loading, setLoading] = useState(false);

    const { data: isBamsReceivingOn, isLoading: isBamsFeatureLoading } = useIsFeatureOn(BAMS_RECEIVING_MODULE);

    const handleOverrideSaved = (poMapID, receivedUnitsPerCase) => {
        setRows((prev) =>
            prev.map((row) => {
                if (row.poMapID !== poMapID) return row;
                return {
                    ...row,
                    receivedUnitsPerCase,
                    packagingPurchaseOrderMaps: row.packagingPurchaseOrderMaps?.map((m) => ({
                        ...m,
                        receivedUnitsPerCase,
                    })),
                };
            }),
        );
    };

    const openQualityDocumentationModal = (row, initialPageIdx) => {
        const poMapID = isBamsReceivingOn ? row.original.qcrPurchaseOrderMapId : row.original.id;
        if (poMapID == null) {
            setNotification({
                text: 'Missing quality control purchase order map id for this item.',
                level: 'error',
            });
            return;
        }

        setModalLaunch({
            poID: parentRow.original.id,
            poMapID,
            qualityReceivingData: {
                ...row.original,
                id: poMapID,
                caseCounts: row.original.caseCounts ?? {},
                externalIssues: row.original.externalIssues ?? {},
                internalIssues: row.original.internalIssues ?? {},
            },
            initialPageIdx,
        });
        setItemDocumentationModalOpen(true);
    };

    let columns = [];
    if (parentRow.original.poType == 'PACKAGING') {
        columns = [
            { accessor: 'poMapID', Header: 'ID' },
            { accessor: 'masterSku', Header: 'Master SKU' },
            { accessor: 'description', Header: 'Name' },
            {
                accessor: 'progressStatus',
                Header: 'Progress Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span
                        style={
                            row.original.progressStatus
                                ? {
                                      backgroundColor: statusColors[row.original.progressStatus.replace(' ', '_')],
                                      color: statusTextColors[row.original.progressStatus.replace(' ', '_')],
                                  }
                                : {}
                        }
                    >
                        {row.original.progressStatus}
                    </span>
                ),
            },
            {
                accessor: 'issueStatus',
                Header: 'Issue Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span style={{ backgroundColor: colors.red, color: 'white' }}>{row.original.issueStatus}</span>
                ),
            },
            { accessor: 'issueTypes', Header: 'Issue Type Status' },
            { accessor: 'qty', Header: 'Expected Case Qty' },
            {
                accessor: 'unitsPerCase',
                Header: 'Ordered Units/Case',
                Cell: ({ row }) => row.original.unitsPerCase ?? '--',
            },
            {
                accessor: 'receivedUnitsPerCase',
                Header: 'Received Units/Case',
                Cell: ({ row }) => (
                    <div onClick={(e) => e.stopPropagation()}>
                        <UnitsPerCaseInput
                            key={`upc-${row.original.poMapID}-${row.original.receivedUnitsPerCase}`}
                            poMapID={row.original.poMapID}
                            packagingPurchaseOrderMapGroupId={row.original.packagingPurchaseOrderMapGroupId}
                            useGroupOverride={Boolean(isBamsReceivingOn)}
                            orderedValue={row.original.unitsPerCase}
                            initialOverride={row.original.receivedUnitsPerCase}
                            progressStatus={row.original.progressStatus}
                            onSaved={handleOverrideSaved}
                            onNotification={(n) => setNotification(n)}
                        />
                    </div>
                ),
            },
            {
                accessor: 'variance',
                Header: 'Variance',
                Cell: ({ row }) => {
                    const { receivedUnitsPerCase, unitsPerCase } = row.original;
                    const result = computeVariance(receivedUnitsPerCase, unitsPerCase);
                    if (!result) return null;
                    const { isWarning, percentage } = result;
                    if (percentage === 0) return '0%';
                    return (
                        <span style={isWarning ? { backgroundColor: VARIANCE_WARNING_COLOR, padding: '2px 6px' } : {}}>
                            {percentage}%{isWarning ? ` Warning: >${VARIANCE_WARNING_THRESHOLD}% variance` : ''}
                        </span>
                    );
                },
            },
        ];
    } else {
        columns = [
            { accessor: 'id', Header: 'ID' },
            { accessor: 'itemMasterSKU', Header: 'Item Master SKU' },
            { accessor: 'itemMasterName', Header: 'Name' },
            {
                accessor: 'progressStatus',
                Header: 'Progress Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span
                        style={
                            row.original.progressStatus
                                ? {
                                      backgroundColor: statusColors[row.original.progressStatus.replace(' ', '_')],
                                      color: statusTextColors[row.original.progressStatus.replace(' ', '_')],
                                  }
                                : {}
                        }
                    >
                        {row.original.progressStatus}
                    </span>
                ),
            },
            { accessor: 'truckID', Header: 'Truck ID', hide: !multiTruckPO },
            { accessor: 'itemNetSuiteID', Header: 'NetSuite Item ID' },
            {
                accessor: 'issueStatus',
                Header: 'Issue Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span style={{ backgroundColor: colors.red, color: 'white' }}>{row.original.issueStatus}</span>
                ),
            },
            { accessor: 'issueTypes', Header: 'Issue Type Status' },
            { accessor: 'qty', Header: 'Expected Cases' },
            { accessor: 'receivedCases', Header: 'Actual Cases', hide: !showReceived },
            { accessor: 'baseUnitsPerCase', Header: 'Expected Units/Case', hide: !showReceived },
            { accessor: 'receivedUnitCount', Header: 'Actual Units/Case', hide: !showReceived },
            { accessor: 'statusChangeAuthUser', Header: 'Auditor' },
            {
                accessor: 'auditCounts',
                Header: 'Units Per Case',
                Cell: ({ row }) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <Button
                            disabled={isBamsReceivingOn && row.original.qcrPurchaseOrderMapId == null}
                            onClick={() => {
                                openQualityDocumentationModal(row, 0);
                            }}
                        >
                            View
                        </Button>
                    </div>
                ),
            },
            {
                accessor: 'externalIssues',
                Header: 'External Issues',
                Cell: ({ row }) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <Button
                            disabled={isBamsReceivingOn && row.original.qcrPurchaseOrderMapId == null}
                            onClick={() => {
                                openQualityDocumentationModal(row, 1);
                            }}
                        >
                            View
                        </Button>
                    </div>
                ),
            },
            {
                accessor: 'internalIssues',
                Header: 'Internal Issues',
                Cell: ({ row }) => (
                    <div onClick={(event) => event.stopPropagation()}>
                        <Button
                            disabled={isBamsReceivingOn && row.original.qcrPurchaseOrderMapId == null}
                            onClick={() => {
                                openQualityDocumentationModal(row, 2);
                            }}
                        >
                            View
                        </Button>
                    </div>
                ),
            },
        ];
    }

    if (allowDidNotArrive && parentRow.original.poType !== 'PACKAGING') {
        columns.push({
            accessor: 'didNotArrive',
            Header: '',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        disabled={
                            (isBamsReceivingOn && row.original.qcrPurchaseOrderMapId == null) ||
                            !(
                                row.original.progressStatus === 'PLACED' ||
                                row.original.progressStatus === 'OPEN' ||
                                row.original.progressStatus === 'IN PROGRESS'
                            )
                        }
                        onClick={() => {
                            setCurrentPurchaseOrderMap({
                                ...row.original,
                                id: isBamsReceivingOn ? row.original.qcrPurchaseOrderMapId : row.original.id,
                            });
                            setShowDidNotArriveModal(true);
                        }}
                    >
                        Product Didn't Arrive
                    </Button>
                </div>
            ),
        });
    }

    if (!showReceived && (editEnabled || isBamsReceivingOn)) {
        columns.push({
            accessor: 'resetQc',
            Header: '',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        disabled={
                            (isBamsReceivingOn && row.original.qcrPurchaseOrderMapId == null) ||
                            (row.original.progressStatus !== 'IN PROGRESS' &&
                                row.original.progressStatus !== 'QC COMPLETE')
                        }
                        onClick={async () => {
                            setCurrentPurchaseOrderMap({
                                ...row.original,
                                id: isBamsReceivingOn ? row.original.qcrPurchaseOrderMapId : row.original.id,
                            });
                            setShowQualityReceivingModuleResetModal(true);
                        }}
                    >
                        Reset
                    </Button>
                </div>
            ),
        });
    }

    if (!showReceived) {
        columns.push({
            accessor: 'receive',
            Header: '',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div>
                    <Button
                        disabled={
                            (parentRow.original.requiresQualityControl &&
                                (row.original?.requiresQCReceiving ?? true) &&
                                row.original.progressStatus !== 'QC COMPLETE') ||
                            row.original.progressStatus == 'RECEIVED' ||
                            row.original.progressStatus == 'CONSUMED'
                        }
                        onClick={async () => {
                            setSelectedRow(row);
                            setReceiveLineItemWithPalletsModalOpen(true);
                        }}
                    >
                        Receive
                    </Button>
                </div>
            ),
        });
    }

    columns.push({
        accessor: 'printLabels',
        Header: 'Print Labels',
        Cell: ({ row }) => {
            const { poType } = parentRow.original;
            const missingGroupId =
                isBamsReceivingOn &&
                (poType === 'PACKAGING'
                    ? (row.original.packagingPurchaseOrderMapGroupId ?? row.original.poMapID) == null
                    : (row.original.purchaseOrderMapGroupId ?? row.original.id) == null);

            if (row.original.progressStatus === 'CONSUMED') {
                return (
                    <Button
                        disabled={missingGroupId}
                        onClick={() => openPrintConsumedLotLabelsModal(poType, row.original)}
                    >
                        Print Consumed
                    </Button>
                );
            }
            if (row.original.progressStatus !== 'RECEIVED') {
                return null;
            }
            return (
                <Button disabled={missingGroupId} onClick={() => openPrintLabelsModal(poType, row.original)}>
                    Print
                </Button>
            );
        },
    });

    const resolvePrintModalDetails = (poType, row) => {
        if (isBamsReceivingOn) {
            const poMapGroupId =
                poType === 'PACKAGING'
                    ? (row.packagingPurchaseOrderMapGroupId ?? row.poMapID)
                    : (row.purchaseOrderMapGroupId ?? row.id);
            return { poMapGroupId, poType };
        }

        const poMapId = poType === 'PACKAGING' ? row.poMapID : row.id;
        return { poMapId, poType };
    };

    const openPrintLabelsModal = (poType, row) => {
        const details = resolvePrintModalDetails(poType, row);
        const lookupId = details.poMapGroupId ?? details.poMapId;
        if (lookupId == null) {
            setNotification({
                text: isBamsReceivingOn
                    ? 'Missing purchase order map group id for this item.'
                    : 'Missing purchase order map id for this item.',
                level: 'error',
            });
            return;
        }
        setPrintLabelModalDetails(details);
    };
    const closePrintLabelsModal = () => {
        setPrintLabelModalDetails(null);
    };

    const openPrintConsumedLotLabelsModal = (poType, row) => {
        const details = resolvePrintModalDetails(poType, row);
        const lookupId = details.poMapGroupId ?? details.poMapId;
        if (lookupId == null) {
            setNotification({
                text: isBamsReceivingOn
                    ? 'Missing purchase order map group id for this item.'
                    : 'Missing purchase order map id for this item.',
                level: 'error',
            });
            return;
        }
        setPrintConsumedLotLabelModalDetails(details);
    };
    const closePrintConsumedLotLabelsModal = () => {
        setPrintConsumedLotLabelModalDetails(null);
    };

    const getRows = async () => {
        setLoading(true);
        try {
            const isPackaging = parentRow.original.poType === 'PACKAGING';
            const result = await fetchReceivableLineItems({
                poId: poID,
                isPackaging,
                isBamsReceivingOn: Boolean(isBamsReceivingOn),
            });

            if (result.errorMessage) {
                setNotification({ text: `No entries found ${result.errorMessage}`, level: 'error' });
                setRows([]);
                return;
            }

            setRows(result.rows);
            if (!isBamsReceivingOn) {
                setEditEnabled(result.editEnabled ?? false);
            }
        } finally {
            setLoading(false);
        }
    };

    const getPhotoRows = async () => {
        const response = await procurementApi.getPurchaseOrderDocuments(poID);

        if (response.status === false) {
            setNotification({ text: `Can't access photos: ${response.msg}`, level: 'error' });
            return;
        }

        setPhotoRows(response.data.rows);
    };

    const photoCols = [
        { accessor: 'id', Header: 'Document ID' },
        { accessor: 'sku', Header: 'SKU' },
        { accessor: 'externalSku', Header: 'External SKU' },
        { accessor: 'name', Header: 'Item Name' },
        { accessor: 'notes', Header: 'Photo Label' },
        {
            accessor: 'documentURL',
            Header: 'Download',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => {
                if (!row.original.documentURL) {
                    return '';
                }
                return (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <Button
                            onClick={() => {
                                procurementApi.downloadPODocument(
                                    poID,
                                    row.original.id,
                                    row.original.documentURL.replace(/^.*[\\\/]/, ''),
                                );
                            }}
                        >
                            Download
                        </Button>
                    </div>
                );
            },
        },
        {
            accessor: 'remove',
            Header: 'Remove',
            Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                <div
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                >
                    <Button
                        onClick={() => {
                            procurementApi.removePODocument(poID, row.original.id);
                        }}
                    >
                        Remove
                    </Button>
                </div>
            ),
        },
    ];

    useEffect(() => {
        if (isBamsFeatureLoading) {
            return;
        }
        getRows();
        getPhotoRows();
        if (parentRow.original.truckID === 'PARTIAL-PO') {
            setMultiTruckPO(true);
        }
    }, [poID, isBamsFeatureLoading, isBamsReceivingOn]);

    const closeReceiveLineItemModal = () => {
        setSelectedRow(null);
        setReceiveLineItemWithPalletsModalOpen(false);
        getRows();
    };

    return (
        <div className="batch-table">
            <Notifications options={notification} />
            {loading || isBamsFeatureLoading ? (
                <div style={{ display: 'flex', justifyContent: 'center', padding: '20px' }}>
                    <CircularProgress />
                </div>
            ) : (
                <>
                    <ItemDidNotArriveModal
                        open={showDidNotArriveModal}
                        handleClose={() => {
                            setShowDidNotArriveModal(false);
                            getRows();
                        }}
                        purchaseOrderId={poID}
                        purchaseOrderMap={currentPurchaseOrderMap}
                    />
                    <QualityReceivingModuleResetModal
                        open={showQualityReceivingModuleResetModal}
                        handleClose={(moduleName) => {
                            setShowQualityReceivingModuleResetModal(false);
                            getRows();
                            getPhotoRows();
                        }}
                        setNotification={setNotification}
                        purchaseOrderMap={currentPurchaseOrderMap}
                    />
                    {itemDocumentationModalOpen && (
                        <DirectedReceivingItemModal
                            rowData={modalLaunch}
                            setNotification={setNotification}
                            closeModal={() => {
                                setItemDocumentationModalOpen(false);
                                getRows();
                            }}
                            issueCodes={issueCodes}
                            editEnabled={editEnabled}
                            photoRows={photoRows}
                        />
                    )}
                    {receiveLineItemWithPalletsModalOpen &&
                        (isBamsReceivingOn ? (
                            <ReceiveAllocatedLineItemModal
                                open={receiveLineItemWithPalletsModalOpen}
                                close={closeReceiveLineItemModal}
                                rowData={selectedRow}
                                poType={parentRow.original.poType}
                                isThirdPartyAsn={parentRow.original.isThirdParty}
                            />
                        ) : (
                            <ReceiveLineItemWithPalletsModal
                                open={receiveLineItemWithPalletsModalOpen}
                                close={closeReceiveLineItemModal}
                                setNotification={setNotification}
                                rowData={selectedRow}
                                poID={poID}
                                poType={parentRow.original.poType}
                                setUpdatedPurchaseOrderStatus={setUpdatedPurchaseOrderStatus}
                                thirdPartyASN={parentRow.original.isThirdParty}
                            />
                        ))}

                    <PrintReceivedPalletAndCaseLabelsModal
                        poMapGroupId={printLabelModalDetails?.poMapGroupId}
                        poMapId={printLabelModalDetails?.poMapId}
                        type={printLabelModalDetails?.poType}
                        onClose={closePrintLabelsModal}
                        open={printLabelModalDetails !== null}
                    />
                    <PrintConsumedPalletAndCaseLabelsModal
                        poMapGroupId={printConsumedLotLabelModalDetails?.poMapGroupId}
                        poMapId={printConsumedLotLabelModalDetails?.poMapId}
                        type={printConsumedLotLabelModalDetails?.poType}
                        onClose={closePrintConsumedLotLabelsModal}
                        open={printConsumedLotLabelModalDetails !== null}
                    />

                    <DataTable
                        columns={columns}
                        data={rows}
                        editable
                        saveEdit={async ({ field, id, value }) => {
                            const updates = {
                                key: field,
                                value,
                            };
                            const response = await qualityApi.recordQualityReceiving(id, updates);
                            if (response.status === false) {
                                let message = 'Error';
                                if (response) {
                                    message += `: ${response.msg}`;
                                }
                                setNotification({ text: message, level: 'error' });
                            } else {
                                setNotification({ text: 'Issue recorded!', level: 'success' });
                                getRows();
                            }
                        }}
                    />
                    <DataTable columns={photoCols} data={photoRows} />
                </>
            )}
        </div>
    );
}

function DirectedReceivablePurchaseOrders({
    showPendingActions = false,
    showPendingIssues = false,
    showReceived = false,
}) {
    const [rows, setRows] = useState([]);
    const [notification, setNotification] = useState({ text: null, level: null });
    const [receiveWithPalletsModalOpen, setReceiveWithPalletsModalOpen] = useState(false);
    const [selectedRow, setSelectedRow] = useState(null);
    const user = useContext(UserContext);
    const [issueCodes, setIssueCodes] = useState([]);
    const [truckTempModalOpen, setTruckTempModalOpen] = useState(false);
    const [showModifyPOModal, setShowModifyPOModal] = useState(false);

    const [modalLaunch, setModalLaunch] = useState({});
    const [itemDocumentationModalOpen, setItemDocumentationModalOpen] = useState(false);
    const [photoRows, setPhotoRows] = useState([]);
    const [editEnabled, setEditEnabled] = useState(false);

    const getPhotoRows = async (poID) => {
        const response = await procurementApi.getPurchaseOrderDocuments(poID);

        if (response.status === false) {
            setNotification({ text: `Can't access photos: ${response.msg}`, level: 'error' });
            return;
        }

        setPhotoRows(response.data.rows);
    };

    let columns = [];
    if (showReceived == true) {
        columns = [
            { accessor: 'id', Header: 'Purchase Order' },
            { accessor: 'truckID', Header: 'Truck ID' },
            { accessor: 'poType', Header: 'Type' },
            { accessor: 'deliveryType', Header: 'Delivery Type' },
            { accessor: 'vendorName', Header: 'Vendor' },
            { accessor: 'shipperName', Header: 'Shipper' },
            { accessor: 'buyer', Header: 'Buyer' },
            {
                accessor: 'status',
                Header: 'Progress Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span
                        style={
                            row.original.status
                                ? {
                                      backgroundColor: statusColors[row.original.status.replace(' ', '_')],
                                      color: statusTextColors[row.original.status.replace(' ', '_')],
                                  }
                                : {}
                        }
                    >
                        {row.original.status}
                    </span>
                ),
            },
            { accessor: 'receivedDate', Header: 'Received Date' },
            {
                accessor: 'issueStatus',
                Header: 'Issue Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span style={{ backgroundColor: colors.red, color: 'white' }}>{row.original.issueStatus}</span>
                ),
            },
            {
                accessor: 'download',
                Header: 'Download',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <Button
                            id="ShipList"
                            onClick={() => {
                                if (row.original.poType === 'PACKAGING') {
                                    packagingApi.downloadPackagingPOShipList(row.original.id);
                                } else {
                                    procurementApi.downloadPOShipList(row.original.id);
                                }
                            }}
                        >
                            Ship List
                        </Button>
                    </div>
                ),
            },
        ];
    } else {
        columns = [
            { accessor: 'id', Header: 'Purchase Order' },
            { accessor: 'truckID', Header: 'Truck ID' },
            { accessor: 'deliveryDateLocal', Header: 'Scheduled Delivery Date' },
            { accessor: 'poType', Header: 'Type' },
            { accessor: 'deliveryType', Header: 'Delivery Type' },
            { accessor: 'vendorName', Header: 'Vendor' },
            { accessor: 'shipperName', Header: 'Shipper' },
            { accessor: 'buyer', Header: 'Buyer' },
            {
                accessor: 'status',
                Header: 'Progress Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span
                        style={
                            row.original.status
                                ? {
                                      backgroundColor: statusColors[row.original.status.replace(' ', '_')],
                                      color: statusTextColors[row.original.status.replace(' ', '_')],
                                  }
                                : {}
                        }
                    >
                        {row.original.status}
                    </span>
                ),
            },
            {
                accessor: 'timeInStatus',
                Header: 'Time in Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span style={row.original.timeInStatus > 90 ? { backgroundColor: colors.red, color: 'white' } : {}}>
                        {row.original.timeInStatus}
                    </span>
                ),
            },

            {
                accessor: 'issueStatus',
                Header: 'Issue Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span style={{ backgroundColor: colors.red, color: 'white' }}>{row.original.issueStatus}</span>
                ),
            },
            {
                accessor: 'download',
                Header: 'Download',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <Button
                            id="ShipList"
                            onClick={() => {
                                if (row.original.poType === 'PACKAGING') {
                                    packagingApi.downloadPackagingPOShipList(row.original.id);
                                } else {
                                    procurementApi.downloadPOShipList(row.original.id);
                                }
                            }}
                        >
                            Ship List
                        </Button>
                    </div>
                ),
            },
            {
                accessor: 'modifyPO',
                Header: 'Modify PO',
                Cell: ({ cell: { row } }) => (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <Button
                            id="Modify"
                            onClick={async () => {
                                setSelectedRow(row.original);
                                setShowModifyPOModal(true);
                            }}
                        >
                            Modify PO
                        </Button>
                    </div>
                ),
            },
        ];
    }

    const getPurchaseOrdersForReceiving = async () => {
        let response;
        response = await receivingApi.getReceivableGroceryAndPackagingPurchaseOrders(user.getFacilityID());

        if (response.status === false) {
            setNotification({ text: `No POs found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        if (response.data.rows) {
            response.data.rows.map((row) => {
                row.deliveryDate = row.deliveryDateLocal?.date
                    ? dayjs(row.deliveryDateLocal.date).format('MM/DD/YYYY HH:mm:ss')
                    : row.deliveryDateLocal || '';
                if (row.truckID === 0 && row.fobPoType === 'partial') {
                    row.truckID = 'PARTIAL-PO';
                }
                if (row.truckID === 0 && row.fobPoType !== 'partial') {
                    row.truckID = null;
                }
                return row;
            });
        }

        // Need to add id which will populate in saveEdit
        setRows(response.data.rows);
    };

    const setUpdatedPurchaseOrderStatus = async (poID, newStatus) => {
        const rowsForUpdate = JSON.parse(JSON.stringify(rows));

        rowsForUpdate.forEach((row) => {
            if (row.id === poID) {
                row.status = newStatus;
            }
        });

        setRows(rowsForUpdate);
    };

    const getPurchaseOrdersAlreadyReceived = async () => {
        const response = await receivingApi.getReceivedGroceryAndPackagingPurchaseOrders(user.getFacilityID());

        if (response.status === false) {
            setNotification({ text: `No POs found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        if (response.data.rows) {
            response.data.rows.map((row) => {
                row.deliveryDate = dayjs(row.deliveryDateLocal.date).format('MM/DD/YYYY HH:mm:ss');
                return row;
            });
        }

        // Need to add id which will populate in saveEdit
        setRows(response.data.rows);
    };

    const getPendingIssues = async () => {
        const response = await qualityApi.getPendingIssues(user.getFacilityID());

        if (response.status === false) {
            setNotification({ text: `No line items found ${response.msg}`, level: 'error' });
            setRows([]);
            return;
        }

        setRows(response.data.rows);
        setEditEnabled(response.data.jalapenoReceivingEditEnabled);
    };

    const getQualityIssueCodes = async () => {
        const response = await qualityApi.getQualityIssueCodes();
        if (response.status === false) {
            setNotification({ text: `No Issue codes found ${response.msg}`, level: 'error' });
            setIssueCodes([]);
        }
        setIssueCodes(response.data.issueCodes);
    };

    const showTruckTempModal = (row) => {
        setSelectedRow(row);
        setTruckTempModalOpen(true);
    };

    useEffect(() => {
        if (showPendingActions == true) {
        } else if (showPendingIssues == true) {
            getPendingIssues();
        } else if (showReceived == true) {
            getPurchaseOrdersAlreadyReceived();
        } else {
            getPurchaseOrdersForReceiving();
        }
        getQualityIssueCodes();
    }, []);

    if (showPendingActions == true || showPendingIssues == true) {
        const pendingActionsColumns = [
            { accessor: 'poID', Header: 'Purchase Order ID' },
            { accessor: 'id', Header: 'ID' },
            { accessor: 'itemMasterSKU', Header: 'Item Master SKU' },
            { accessor: 'itemMasterName', Header: 'Name' },
            { accessor: 'vendorName', Header: 'Vendor' },
            {
                accessor: 'progressStatus',
                Header: 'Progress Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span
                        style={
                            row.original.progressStatus
                                ? {
                                      backgroundColor: statusColors[row.original.progressStatus.replace(' ', '_')],
                                      color: statusTextColors[row.original.progressStatus.replace(' ', '_')],
                                  }
                                : {}
                        }
                    >
                        {row.original.progressStatus}
                    </span>
                ),
            },
            {
                accessor: 'issueStatus',
                Header: 'Issue Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span style={{ backgroundColor: colors.red, color: 'white' }}>{row.original.issueStatus}</span>
                ),
            },
            {
                accessor: 'timeInStatus',
                Header: 'Time in Status',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <span style={row.original.timeInStatus > 90 ? { backgroundColor: colors.red, color: 'white' } : {}}>
                        {row.original.timeInStatus}
                    </span>
                ),
            },
            { accessor: 'issueTypes', Header: 'Issue Type Status' },
            { accessor: 'qty', Header: 'Expected Cases' },
            { accessor: 'receivedCases', Header: 'Actual Cases' },
            { accessor: 'baseUnitsPerCase', Header: 'Expected Units/Case' },
            { accessor: 'receivedUnitCount', Header: 'Actual Units/Case' },
            { accessor: 'statusChangeAuthUser', Header: 'Auditor' },
            {
                accessor: 'externalIssues',
                Header: 'External Issues',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <Button
                            onClick={async () => {
                                await getPhotoRows(row.original.poID);
                                setModalLaunch({
                                    poID: row.original.poID,
                                    poMapID: row.original.id,
                                    qualityReceivingData: row.original,
                                    initialPageIdx: 1,
                                });
                                setItemDocumentationModalOpen(true);
                            }}
                        >
                            View
                        </Button>
                    </div>
                ),
            },
            {
                accessor: 'internalIssues',
                Header: 'Internal Issues',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <Button
                            onClick={async () => {
                                await getPhotoRows(row.original.poID);
                                setModalLaunch({
                                    poID: row.original.poID,
                                    poMapID: row.original.id,
                                    qualityReceivingData: row.original,
                                    initialPageIdx: 2,
                                });
                                setItemDocumentationModalOpen(true);
                            }}
                        >
                            View
                        </Button>
                    </div>
                ),
            },
            {
                accessor: 'auditCounts',
                Header: 'Units Per Case',
                Cell: ({ cell: { value: initialValue }, column, editable, row, saveEdit }) => (
                    <div
                        onClick={(event) => {
                            event.stopPropagation();
                        }}
                    >
                        <Button
                            onClick={async () => {
                                await getPhotoRows(row.original.poID);
                                setModalLaunch({
                                    poID: row.original.poID,
                                    poMapID: row.original.id,
                                    qualityReceivingData: row.original,
                                    initialPageIdx: 0,
                                });
                                setItemDocumentationModalOpen(true);
                            }}
                        >
                            View
                        </Button>
                    </div>
                ),
            },
        ];

        if (showPendingIssues == true) {
            return (
                <div>
                    <hi>Issue Lines</hi>
                    {itemDocumentationModalOpen && (
                        <DirectedReceivingItemModal
                            rowData={modalLaunch}
                            setNotification={setNotification}
                            closeModal={() => {
                                setItemDocumentationModalOpen(false);
                                getPendingIssues();
                            }}
                            issueCodes={issueCodes}
                            editEnabled={editEnabled}
                            photoRows={photoRows}
                        />
                    )}

                    <Notifications options={notification} />
                    <DataTable columns={pendingActionsColumns} data={rows} expandable={false} />
                </div>
            );
        }
        return (
            <div>
                <hi>Pending Actions</hi>
                {itemDocumentationModalOpen && (
                    <DirectedReceivingItemModal
                        rowData={modalLaunch}
                        setNotification={setNotification}
                        closeModal={() => {
                            setItemDocumentationModalOpen(false);
                        }}
                        issueCodes={issueCodes}
                        editEnabled={editEnabled}
                        photoRows={photoRows}
                    />
                )}

                <Notifications options={notification} />
                <DataTable columns={pendingActionsColumns} data={rows} expandable={false} />
            </div>
        );
    }
    return (
        <div>
            <h1>{showReceived ? 'Completed Purchase Orders' : 'Receivable Purchase Orders'}</h1>
            <Notifications options={notification} />
            <div>
                {showModifyPOModal && (
                    <ModifyPOModal
                        open={showModifyPOModal}
                        close={() => setShowModifyPOModal(false)}
                        rowData={selectedRow}
                    />
                )}
                <DataTable
                    columns={columns}
                    data={rows}
                    expandable
                    ExpansionComponent={
                        <ViewItemsTableV2
                            issueCodes={issueCodes}
                            setUpdatedPurchaseOrderStatus={setUpdatedPurchaseOrderStatus}
                            showReceived={showReceived}
                        />
                    }
                />
            </div>
        </div>
    );
}

export { computeVariance, DirectedReceivablePurchaseOrders, UnitsPerCaseInput, VARIANCE_WARNING_THRESHOLD };
